import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ButtonVariant } from './Button';

export interface DialogProps {
  /** Omit for a controlled dialog whose trigger lives on the calling surface. */
  triggerLabel?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  triggerVariant?: ButtonVariant;
  closeLabel: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Keep modal input intact when the user presses the backdrop. */
  disablePointerDismissal?: boolean;
  showCloseButton?: boolean;
  showHeaderCloseButton?: boolean;
  headerCloseVariant?: 'soft' | 'danger-hover';
  popupClassName?: string;
}

export function Dialog({
  triggerLabel,
  title,
  description,
  children,
  footer,
  triggerVariant = 'secondary',
  closeLabel,
  open,
  onOpenChange,
  disablePointerDismissal = false,
  showCloseButton = true,
  showHeaderCloseButton = false,
  headerCloseVariant = 'soft',
  popupClassName,
}: DialogProps) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange} disablePointerDismissal={disablePointerDismissal}>
      {triggerLabel ? (
        <BaseDialog.Trigger className={`ui-button ui-button--${triggerVariant} ui-button--md`}>
          <span className="ui-button__label">{triggerLabel}</span>
        </BaseDialog.Trigger>
      ) : null}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="ui-dialog__backdrop" />
          <BaseDialog.Popup
            aria-modal="true"
            className={`ui-dialog__popup${popupClassName ? ` ${popupClassName}` : ''}`}
          >
            <div className="ui-dialog__header">
              <div className="ui-dialog__header-content">
                <BaseDialog.Title className="ui-dialog__title">{title}</BaseDialog.Title>
                {description ? (
                  <BaseDialog.Description className="ui-dialog__description">
                    {description}
                  </BaseDialog.Description>
                ) : null}
              </div>
              {showHeaderCloseButton ? (
                <BaseDialog.Close
                  className={`ui-dialog__header-close ui-icon-button ui-icon-button--${headerCloseVariant} ui-icon-button--md`}
                  aria-label={closeLabel}
                >
                  <X size={18} aria-hidden="true" />
                </BaseDialog.Close>
              ) : null}
            </div>
          <div className="ui-dialog__body">{children}</div>
          <div className="ui-dialog__footer">
            {footer}
            {showCloseButton ? (
              <BaseDialog.Close className="ui-button ui-button--secondary ui-button--md">
                <span className="ui-button__label">{closeLabel}</span>
              </BaseDialog.Close>
            ) : null}
          </div>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
